import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";
import { pushToDataLayer } from "../lib/dataLayer";
import { blockGoogleMeasurement, allowGoogleMeasurement } from "../lib/tagBlocker";

const STORAGE_KEY = "cass_cookie_consent";
export const CONSENT_EVENT = "cass:cookie-settings";

// Switches off every Google tag running on the page, by ID.
//
// A GA4 runtime that is already loaded fires a final user_engagement beacon as
// the page unloads, and can flush anything it had queued — so withdrawing consent
// has to silence it before the reload, not just afterwards. The measurement IDs
// are discovered from the cookies Google itself set (`_ga_ABC123` means
// `G-ABC123`), so a property linked at the account level is caught too, without
// that ID having to be known here.
function disableGoogleTags() {
  const ids = new Set(window.cassTagIds || []);
  document.cookie.split(";").forEach((entry) => {
    const match = entry.trim().match(/^_ga_([A-Z0-9]+)=/);
    if (match) ids.add(`G-${match[1]}`);
  });
  ids.forEach((id) => { window[`ga-disable-${id}`] = true; });
}

// Consent Mode v2: all four signals, not just analytics, so Google's tags behave
// consistently and an advertising tag can't store anything a visitor refused.
//
// Basic Consent Mode: on accept the Google scripts are loaded for the first time;
// on reject nothing is loaded at all, so not even a cookieless ping is sent.
function updateConsent(granted) {
  const state = granted ? "granted" : "denied";
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: state,
      ad_user_data: state,
      ad_personalization: state,
      analytics_storage: state,
    });
  }
  // Tag Manager sees the choice too, so tags built in the container can wait for
  // it rather than firing regardless.
  pushToDataLayer("cookie_consent_update", { consent_state: state });
  if (granted) {
    allowGoogleMeasurement();
    if (typeof window.cassLoadTags === "function") window.cassLoadTags();
  } else {
    clearGoogleCookies();
  }
}

// Withdrawing consent should take the cookies away too, not just stop new ones.
function clearGoogleCookies() {
  const host = window.location.hostname;
  const domains = [host, `.${host}`, `.${host.split(".").slice(-2).join(".")}`];
  document.cookie.split(";").forEach((entry) => {
    const name = entry.split("=")[0].trim();
    if (!/^(_ga|_gid|_gcl|_gac|__utm)/.test(name)) return;
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domain}`;
    });
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  });
}

// Anywhere on the site can reopen this: window.dispatchEvent(new Event(CONSENT_EVENT)).
export function openCookieSettings() {
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

export function consentChoice() {
  try {
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

// Cookie banner wired to Google Consent Mode. Analytics and advertising cookies
// only after the visitor accepts; the choice is remembered and can be changed at
// any time from the footer.
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [choice, setChoice] = useState("");
  // Staff area: the banner sat over the sticky save bars and swallowed clicks.
  const inAdmin = useLocation().pathname.startsWith("/admin");

  useEffect(() => {
    const existing = consentChoice();
    setChoice(existing);
    if (existing === "granted") {
      updateConsent(true);
    } else {
      // Refused, now or on an earlier visit: nothing Google-owned may leave the
      // page, whatever else gets loaded.
      blockGoogleMeasurement();
      if (!existing) setVisible(true);
    }
  }, []);

  useEffect(() => {
    const reopen = () => {
      setChoice(consentChoice());
      setVisible(true);
    };
    window.addEventListener(CONSENT_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_EVENT, reopen);
  }, []);

  const decide = (granted) => {
    // Silence anything already running before the choice is recorded, so no
    // parting beacon escapes while this page is being replaced, and drop any
    // batch gtag still has queued.
    if (!granted) {
      disableGoogleTags();
      blockGoogleMeasurement();
    }
    localStorage.setItem(STORAGE_KEY, granted ? "granted" : "denied");
    setChoice(granted ? "granted" : "denied");
    updateConsent(granted);
    setVisible(false);
    // Withdrawing consent part-way through a visit: Google's scripts are already
    // in memory and a loaded tag can't be unloaded — left alone it keeps sending
    // cookieless pings. Reloading drops them, and nothing fetches them again.
    if (!granted && window.cassTagsLoaded) {
      window.location.reload();
    }
  };

  if (!visible || inAdmin) return null;

  return (
    <div data-testid="cookie-consent-banner" className="fixed bottom-0 inset-x-0 z-[60] p-4 sm:p-5">
      <div className="max-w-4xl mx-auto bg-navy-deep text-slate-200 rounded-2xl shadow-2xl border border-white/10 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <span className="w-10 h-10 rounded-xl bg-orange/15 text-orange flex items-center justify-center shrink-0"><Cookie size={20} /></span>
        <div className="flex-1 text-sm leading-relaxed">
          We use essential cookies to make our site work. With your consent, we'd also like to use analytics cookies to understand how visitors use our site so we can improve it. See our{" "}
          <Link to="/privacy-policy" className="text-orange font-semibold hover:underline" data-testid="cookie-privacy-link">Privacy Policy</Link>.
          {choice && (
            <span className="block mt-1 text-slate-400" data-testid="cookie-current-choice">
              You currently {choice === "granted" ? "allow" : "don't allow"} analytics cookies. Changing it below replaces that choice.
            </span>
          )}
        </div>
        <div className="flex gap-2 shrink-0 w-full sm:w-auto">
          <button data-testid="cookie-reject" onClick={() => decide(false)} className="flex-1 sm:flex-none px-5 py-2.5 rounded-full text-sm font-bold text-slate-200 border border-white/20 hover:bg-white/10 transition-colors">Reject</button>
          <button data-testid="cookie-accept" onClick={() => decide(true)} className="flex-1 sm:flex-none px-6 py-2.5 rounded-full text-sm font-bold bg-orange hover:bg-orange-dark text-white transition-colors">Accept all</button>
        </div>
      </div>
    </div>
  );
}
